import type { FieldUuid, OptionUuid } from '@atlassian/jira-shared-types/src/general.tsx';

/**
 * This is a Field Category identifier; must be unique. Used as field.category, and also .name in fieldCategoryConfiguration.
 * This particular type is mostly used in project-settings-apps/issue-type-page and product-features;
 * but the same strings will appear somewhere in Bento (src/issue).
 *
 * See https://hello.atlassian.net/wiki/spaces/JPLAT/pages/375800403/Field+categories+configuration
 */
export type FieldCategory = string;
export const toFieldCategory = (c: string): FieldCategory => c;

export type OptionType = {
	readonly value: string;
	readonly id: number | null;
	readonly parentId?: number | null;
	readonly externalUuid?: OptionUuid;
	readonly parentExternalUuid?: OptionUuid | null;
};

export type FieldOptions = ReadonlyArray<OptionType>;

export type FieldOperations = Readonly<{
	editable: boolean;
	canModifyOptions: boolean;
	canModifyDefaultValue: boolean;
	canModifyPropertyConfiguration: boolean;
	canModifyRequired: boolean;
	removable: boolean;
	categoriesWhitelist: FieldCategory[];
	canAssociateInSettings: boolean;
	deletable: boolean;
}>;

export type FieldProvider = {
	key: string;
	name: string;
};

/**
 * Field. Because all of this is DonutWorld, this Field is scoped to a Project.
 * May and may not be associated with an IssueType.
 */
type MinimalField = {
	externalUuid: FieldUuid;
	// type may be 'text' | 'checkbox' | 'date' ... data type of the field
	// no PII
	type: string;
	// Human readable name. May contain PII
	name: string;
	// there's a button in issue view that says 'show more' and ... well, shows more fields.
	// 'category' property says if this field is above or below the fold
	category: FieldCategory;
	// true = this field must be filled in all issues, false = may stay empty
	required: boolean;
	// true = custom, false = system field
	custom: boolean;
	description: string;
	// There are different structures for different fields but all of them are arrays
	options?: FieldOptions;
	operations: FieldOperations;
	configuration?: {
		[key: string]: unknown;
	};
	// There are different structures for different fields
	defaultValue?: unknown;
	// anyone can write anything into properties
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	properties?: null | Record<any, any>;
	// Provider of the field used to tell users where it came from. This may be the platform, product that created the
	// field or third-party app.
	provider?: FieldProvider;
};

export type CreatedField = MinimalField;

// AssociatedField has mandatory associationId but this endpoint does not require it -> therefore separate type
export type UpdatedField = CreatedField & {
	key: string;
	global: boolean;
};
